import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { LegalLinks } from "@/components/LegalLinks";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user.platform_role === "admin") navigate("/platform");
      else if (["owner", "staff"].includes(data.user.tenant_role)) navigate("/dashboard");
      else if (data.storefront) navigate(`/store/${data.storefront.slug}`);
      else navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-sm mx-auto">
          <Logo size="lg" className="mb-10" />
          <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-2">Sign in to your Drelik account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="username" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-primary font-medium hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <Input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input" className="h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            New to Drelik?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-to-register">Create a store</Link>
          </p>
          <div className="mt-8"><LegalLinks /></div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative h-full flex flex-col justify-center px-16 text-primary-foreground">
          <h2 className="text-4xl font-bold leading-tight tracking-tight">Run smarter commerce.</h2>
          <p className="mt-4 text-lg text-primary-foreground/80 max-w-md">
            Customer-specific pricing, private catalogs, inventory, and CRM in one elegant platform.
          </p>
        </div>
      </div>
    </div>
  );
}
